import { Appearance, Box, Button, Text, Icon, Checkbox } from "@blasterjs/core";
import React, { useState } from "react";
import TooltipFlyout, { TooltipLink } from "../components/Tooltip";
import ConfirmationDialog from "./ConfirmationDialog";
import styled from "styled-components";
import { UUID, Fragment, HpfAnno, PointAnno, Indication, CaseStatus } from "../models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
import {
  faCaretDown,
  faCircle as solidCircle,
  faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import { faCircle, faCircleDot } from "@fortawesome/free-regular-svg-icons";
import { useAppDispatch, useAppSelector } from "../hooks";
import type { AppDispatch } from "../store";
import {
  deleteAnnotation,
  setImageAnnotationHighlight,
  setImageAnnotationUnhighlight,
  toggleCeliacTableVisibility,
  toggleHpfVisibility,
  toggleAcceptAiAnnotations,
  refreshImage
} from "../slices/caseImageViewer";
import { mapStateToUnifiedFrags } from "../utils";
import InfoSidebarCeliacTable from "./InfoSidebarCeliacTable";
import { LoggedInUser, RolePermissions } from "../permissions";
import { bulkDeletePointsApi } from "../api";
import "./AnnotationTree.css";

library.add(faUser);
library.add(faCircle);
library.add(solidCircle);
library.add(faCircleDot);
library.add(faCaretRight);
library.add(faCaretDown);
library.add(faWandMagicSparkles);

const ORPHAN = "Orphan";

interface Props {
  readonly imageId: UUID | null;
  readonly userCanDeleteAnnotations: boolean;
  readonly caseStatus: CaseStatus | null;
  readonly loggedInUser: LoggedInUser;
}

const TreeBox = styled.div`
  display: flex;
  padding: 1px;
  flex-direction: column;
  width: 300px;
`;

const TreeRow = styled.div`
  display: block;
  padding: 1px;
  flex-direction: row;
  vertical-align: middle;
  text-align: left;
  float: left;
  height: 32px;
  width: 300px;
  layout: row;
`;

const PointTreeRow = styled.div`
  display: block;
  padding: 1px;
  padding-left: 60px;
  flex-direction: row;
  vertical-align: middle;
  text-align: left;
  float: left;
  height: 32px;
  width: 300px;
  layout: row;
`;

const AnnoNextHeader = styled.div`
  display: flex;
  align-items: flex-end;
`;

const TreeCellPart = styled.div`
  float: left;
`;

const HpfTreeRow = styled.div`
  display: block;
  padding: 2px;
  padding-left: 30px;
  flex-direction: row;
  vertical-align: middle;
  text-align: left;
  float: left;
  height: 32px;
  width: 300px;
  layout: row;
`;

const YellowBackgroundDiv = styled.div`
  display: inline;
  background-color: #ffefd5;
  border: 2px solid #ffefd5;
  margin: 4px;
  padding: 4px;
  font-size: 14px !important;
`;

// Helper functions for manual annotations
const deleteAllHpfs = (f: Fragment, dispatch: AppDispatch) => () => {
  f.hpfs &&
    f.hpfs.forEach(hpf => {
      dispatch(deleteAnnotation({ annotationId: hpf.id, deleteNested: false }));
    });
};

const deleteAllHpfsAndPoints = (
  f: Fragment,
  imageId: UUID | null,
  dispatch: AppDispatch
) => async () => {
  if (!imageId || !f.hpfs) return;

  await Promise.all(
    f.hpfs.map(async hpf => {
      if (hpf.id === "no-id2" || hpf.id === "no-id") {
        await bulkDeletePointsApi(imageId, null);
      } else {
        await bulkDeletePointsApi(imageId, hpf.id);
        await dispatch(deleteAnnotation({ annotationId: hpf.id, deleteNested: false }));
      }
    })
  );
  await dispatch(refreshImage({ imageId }));
};

const deleteHpf = (hpf: HpfAnno, dispatch: AppDispatch) => () => {
  dispatch(deleteAnnotation({ annotationId: hpf.id, deleteNested: false }));
};

const deleteHpfAndPoints = (
  hpf: HpfAnno,
  imageId: UUID | null,
  dispatch: AppDispatch
) => async () => {
  if (!imageId) return;

  if (hpf.id === "no-id2" || hpf.id === "no-id") {
    // For orphan HPFs, use bulk delete for unassigned points
    await bulkDeletePointsApi(imageId, null);
    await dispatch(refreshImage({ imageId }));
  } else {
    // For regular HPFs, use bulk delete for points in this HPF then delete the HPF
    await bulkDeletePointsApi(imageId, hpf.id);
    await dispatch(deleteAnnotation({ annotationId: hpf.id, deleteNested: false }));
  }
};

const deletePoints = (
  pointAnno: PointAnno,
  hpf: HpfAnno,
  imageId: UUID | null,
  dispatch: AppDispatch
) => async () => {
  if (!imageId) return;

  const source = pointAnno.name.includes("-AI") ? "VisioPharm" : "CR";
  await bulkDeletePointsApi(
    imageId,
    hpf.id === "no-id2" || hpf.id === "no-id" ? null : hpf.id,
    pointAnno.id,
    source
  );
  await dispatch(refreshImage({ imageId }));
};

export function copyText(t: string): string {
  const p = navigator.clipboard.writeText(t);
  const pr1 = p.catch(err => err).then(() => "done");
  return t + pr1.toString();
}

function toggleOpenTree(hele: HTMLElement, branchId: string) {
  const branchEle = document.getElementById(branchId);
  if (hele.classList.contains("annotationtree__treeIsOpen")) {
    hele.classList.remove("annotationtree__treeIsOpen");
    hele.classList.add("annotationtree__treeIsClosed");
    branchEle && branchEle.classList.remove("annotationtree__treeBranchOpen");
    branchEle && branchEle.classList.add("annotationtree__treeBranchClosed");
  } else {
    hele.classList.remove("annotationtree__treeIsClosed");
    hele.classList.add("annotationtree__treeIsOpen");
    branchEle && branchEle.classList.remove("annotationtree__treeBranchClosed");
    branchEle && branchEle.classList.add("annotationtree__treeBranchOpen");
  }
}

function highlightHpf(hele: HTMLElement, dispatch: AppDispatch) {
  const hpfId = hele.getAttribute("data-hpf-id");
  if (hpfId) {
    dispatch(setImageAnnotationHighlight(hpfId));
  }
}

function unhighlightHpf(hele: HTMLElement, dispatch: AppDispatch) {
  const hpfId = hele.getAttribute("data-hpf-id");
  if (hpfId) {
    dispatch(setImageAnnotationUnhighlight(hpfId));
  }
}

function noop(): void {
  // don't do anything
}

export const UnifiedAnnotationTree = ({
  imageId,
  userCanDeleteAnnotations,
  caseStatus,
  loggedInUser
}: Props) => {
  const dispatch = useAppDispatch();
  const unifiedFragments = useAppSelector(state => mapStateToUnifiedFrags(state));

  const [isConfirmationDialog, setConfirmationDialogOpen] = useState({
    open: false,
    message: "Are you sure you want to delete the annotation?",
    onConfirm: noop
  });

  const onConfirm = (f: () => void) => () => {
    setConfirmationDialogOpen({ open: false, message: "the annotations", onConfirm: noop });
    f();
  };

  const onTransition = (m: string, f: () => void) => () =>
    setConfirmationDialogOpen({
      open: true,
      message: "Are you sure you want to delete " + m + "?",
      onConfirm: onConfirm(f)
    });

  const onCancel = () => {
    setConfirmationDialogOpen({ open: false, message: "", onConfirm: noop });
  };

  const confirmTransitionDialog = (
    <ConfirmationDialog
      title="Confirm Annotation Delete"
      message={isConfirmationDialog.message}
      isOpen={isConfirmationDialog.open}
      onConfirm={isConfirmationDialog.onConfirm}
      onCancel={onCancel}
    />
  );

  const manualHpfVisibility = useAppSelector(state => state.caseImageViewer.manualHpfVisibility);
  const aiHpfVisibility = useAppSelector(state => state.caseImageViewer.aiHpfVisibility);
  const celiacTableVisibility = useAppSelector(
    state => state.caseImageViewer.celiacTableVisibility
  );
  const imageWithAnnotations = useAppSelector(state => state.caseImageViewer.imageWithAnnotations);

  const acceptAiAnnotations =
    imageWithAnnotations !== null && "resource" in imageWithAnnotations
      ? imageWithAnnotations.resource.acceptAiAnnotations
      : false;

  const appSequenceId =
    imageWithAnnotations !== null && "resource" in imageWithAnnotations
      ? imageWithAnnotations.resource.mldFile?.appSequenceId
      : "";

  const hasAiAnnotations =
    imageWithAnnotations !== null &&
    "resource" in imageWithAnnotations &&
    imageWithAnnotations.resource.mldFile;

  const canAcceptAiAnnotations =
    caseStatus &&
    caseStatus == CaseStatus.Processed &&
    loggedInUser.can([RolePermissions.AP_CaseViewer_MarkCaseasScored]);

  const renderFragment = (fragment: Fragment, fragmentIndex: number, branchPrefix: string) => {
    const hasOrphan: boolean =
      (fragment?.hpfs && fragment.hpfs.length > 0 && fragment.hpfs[0]?.name === ORPHAN) || false;
    const isFreehand = fragment.name.includes("Freehand");
    const annoType = isFreehand ? "Freehand" : "HPF";

    return (
      <div key={`${fragment.isAI ? "ai" : "manual"}_${fragmentIndex}`}>
        <TreeRow className="annotationtree__highlight_anno_1 annotationtree__treeRow">
          {fragment.isOpen ? (
            <>
              <TreeCellPart>
                <Box
                  className="annotationtree__treeController annotationtree__treeIsOpen"
                  data-frag-name="abc"
                  onClick={(e: MouseEvent) =>
                    e &&
                    e.currentTarget &&
                    toggleOpenTree(
                      e.currentTarget as HTMLElement,
                      branchPrefix + fragmentIndex.toString()
                    )
                  }
                >
                  <Box className="annotationtree__closedTree">
                    <FontAwesomeIcon icon={faCaretRight} />
                  </Box>
                  <Box className="annotationtree__openTree">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </Box>
                </Box>
              </TreeCellPart>
              <Box className="annotationtree__treeCell" color={fragment.color}>
                <FontAwesomeIcon icon={faCircle} />
                &nbsp;
                <Text style={{ color: "black" }}>{fragment.name}</Text>
                {fragment.isAI && fragment.name !== "HPF + Points" && (
                  <FontAwesomeIcon
                    icon={faWandMagicSparkles}
                    color="gray"
                    style={{ marginLeft: "8px" }}
                  />
                )}
                {!userCanDeleteAnnotations ||
                (fragment.isAI && fragment.name !== "HPF + Points") ||
                isFreehand ? (
                  <></>
                ) : (
                  <Button data-tooltip={true} appearance={Appearance.MINIMAL}>
                    <Icon name="menu" />
                    <TooltipFlyout placement={"left-start"}>
                      <TooltipLink
                        onClick={onTransition(
                          "all the HPFS on " + fragment.name,
                          deleteAllHpfs(fragment, dispatch)
                        )}
                      >
                        Delete All HPFs
                      </TooltipLink>
                      <TooltipLink
                        onClick={onTransition(
                          "all the HPFS and points on " + fragment.name,
                          deleteAllHpfsAndPoints(fragment, imageId, dispatch)
                        )}
                      >
                        Delete All HPFs and Points
                      </TooltipLink>
                    </TooltipFlyout>
                  </Button>
                )}
              </Box>
              {!isFreehand && (
                <Box className="hangRight">
                  <Checkbox
                    checked={
                      !(fragment.isAI ? aiHpfVisibility : manualHpfVisibility)?.find(
                        x =>
                          x.hpfClassid === fragment.id &&
                          x.hpfId === null &&
                          x.pointClassId === null
                      )
                    }
                    onChange={() => {
                      dispatch(
                        toggleHpfVisibility({
                          source: fragment.isAI ? "VisioPharm" : "CR",
                          fragment: fragment,
                          hpfVisibility: {
                            hpfClassid: fragment.id || null,
                            hpfId: null,
                            pointClassId: null
                          }
                        })
                      );
                    }}
                  ></Checkbox>
                </Box>
              )}
            </>
          ) : (
            <>
              <Button
                onClick={(e: MouseEvent) =>
                  e && e.target && (e.target as HTMLSpanElement).innerText
                }
              >
                open f
              </Button>
              <Box>{fragment.name}</Box>
            </>
          )}
        </TreeRow>
        <Box
          id={branchPrefix + fragmentIndex.toString()}
          className="annotationtree__treeBranchOpen"
        >
          {fragment.hpfs?.map((hpf, hpfIndex) => (
            <div key={`${hpf.isAI ? "ai" : "manual"}_${fragmentIndex}_${hpfIndex}`}>
              <HpfTreeRow className="annotationtree__highlight_anno_1 annotationtree__treeRow">
                <Box
                  style={{
                    border: "thin empty black",
                    height: "30px",
                    backgroundColor: "transparent"
                  }}
                  data-hpf-id={hpf.id}
                  onMouseEnter={
                    !hpf.isAI
                      ? (e: MouseEvent) => highlightHpf(e.currentTarget as HTMLElement, dispatch)
                      : undefined
                  }
                  onMouseLeave={
                    !hpf.isAI
                      ? (e: MouseEvent) => unhighlightHpf(e.currentTarget as HTMLElement, dispatch)
                      : undefined
                  }
                >
                  <TreeCellPart>
                    <Box
                      className="annotationtree__treeController annotationtree__treeIsOpen"
                      data-frag-name="abc"
                      onClick={(e: MouseEvent) =>
                        e &&
                        e.currentTarget &&
                        toggleOpenTree(
                          e.currentTarget as HTMLElement,
                          branchPrefix + fragmentIndex.toString() + "_" + hpfIndex.toString()
                        )
                      }
                    >
                      <Box className="annotationtree__closedTree">
                        <FontAwesomeIcon icon={faCaretRight} />
                      </Box>
                      <Box className="annotationtree__openTree">
                        <FontAwesomeIcon icon={faCaretDown} />
                      </Box>
                    </Box>
                  </TreeCellPart>
                  <Box className="annotationtree__treeCell" color={hpf.color}>
                    <FontAwesomeIcon icon={solidCircle} />
                    &nbsp;
                    <Text style={{ color: "black" }}>
                      {(hpf.name || `${annoType} ${hpfIndex + (hasOrphan ? 0 : 1)}`) +
                        (hpf.isAI && hpf.name !== ORPHAN ? "-AI" : "")}
                    </Text>
                    {hpf.isAI && (
                      <FontAwesomeIcon
                        icon={faWandMagicSparkles}
                        color="gray"
                        style={{ marginLeft: "8px" }}
                      />
                    )}
                    {!userCanDeleteAnnotations || isFreehand ? (
                      <></>
                    ) : (
                      <Button data-tooltip={true} appearance={Appearance.MINIMAL}>
                        <Icon name="menu" />
                        <TooltipFlyout placement={"left-start"}>
                          {hpf.name === ORPHAN ? (
                            <TooltipLink
                              onClick={onTransition(
                                "all unassigned points in " + hpf.name,
                                deleteHpfAndPoints(hpf, imageId, dispatch)
                              )}
                            >
                              Delete all unassigned Points
                            </TooltipLink>
                          ) : (
                            <>
                              {hpf.id !== "no-id" && (
                                <TooltipLink
                                  onClick={onTransition(
                                    "the " +
                                      (hpf.name ||
                                        `${annoType} ${hpfIndex + (hasOrphan ? 0 : 1)}`) +
                                      ` ${annoType}`,
                                    deleteHpf(hpf, dispatch)
                                  )}
                                >
                                  Delete {annoType}
                                </TooltipLink>
                              )}
                              <TooltipLink
                                onClick={onTransition(
                                  "the " +
                                    (hpf.name || `${annoType} ${hpfIndex + (hasOrphan ? 0 : 1)}`) +
                                    ` ${annoType} and its points`,
                                  deleteHpfAndPoints(hpf, imageId, dispatch)
                                )}
                              >
                                Delete {annoType} and Points
                              </TooltipLink>
                            </>
                          )}
                        </TooltipFlyout>
                      </Button>
                    )}
                  </Box>
                  <Box className="hangRight" style={{ textAlign: "right" }}>
                    &nbsp;
                    <Text
                      onClick={(e: MouseEvent) =>
                        e && e.target && copyText((e.target as HTMLSpanElement).innerText)
                      }
                      style={{
                        backgroundColor: "#d0d0d0",
                        padding: "2px 4px",
                        borderRadius: "3px",
                        minWidth: "40pt",
                        textAlign: "center",
                        display: "inline-block"
                      }}
                    >
                      {hpf.count}
                    </Text>
                    {!isFreehand && (
                      <Checkbox
                        checked={
                          !(hpf.isAI ? aiHpfVisibility : manualHpfVisibility)?.find(
                            x =>
                              x.hpfClassid === fragment.id &&
                              x.hpfId === hpf.id &&
                              x.pointClassId === null
                          ) &&
                          !manualHpfVisibility?.find(
                            x =>
                              x.hpfClassid === fragment.id &&
                              x.hpfId === null &&
                              x.pointClassId === null
                          ) &&
                          !aiHpfVisibility?.find(
                            x =>
                              x.hpfClassid === fragment.id &&
                              x.hpfId === null &&
                              x.pointClassId === null
                          )
                        }
                        onChange={() => {
                          dispatch(
                            toggleHpfVisibility({
                              source: hpf.isAI ? "VisioPharm" : "CR",
                              fragment: fragment,
                              hpfVisibility: {
                                hpfClassid: fragment.id || null,
                                hpfId: hpf.id,
                                pointClassId: null
                              }
                            })
                          );
                        }}
                      ></Checkbox>
                    )}
                  </Box>
                </Box>
              </HpfTreeRow>
              <Box
                id={branchPrefix + fragmentIndex.toString() + "_" + hpfIndex.toString()}
                className="annotationtree__treeBranchOpen"
              >
                {hpf.points?.map((pt, pointIndex) => (
                  <PointTreeRow
                    key={`${hpf.isAI ? "ai" : "manual"}_${fragmentIndex}_${hpfIndex}_${pointIndex}`}
                    className="annotationtree__highlight_anno_1 annotationtree__treeRow"
                  >
                    <Box
                      className="annotationtree__treeCell"
                      data-hpf-pt-cls-id={hpf.id + pt.id}
                      // Disabled to prevent lag with large point sets - can be re-enabled later
                      // onMouseEnter={
                      //   !hpf.isAI
                      //     ? (e: MouseEvent) =>
                      //         highlightPoints(e.currentTarget as HTMLElement, pt, dispatch)
                      //     : undefined
                      // }
                      // onMouseLeave={
                      //   !hpf.isAI
                      //     ? (e: MouseEvent) =>
                      //         unhighlightPoints(e.currentTarget as HTMLElement, pt, dispatch)
                      //     : undefined
                      // }
                      color={pt.color}
                    >
                      <FontAwesomeIcon
                        height="9"
                        width="9"
                        icon={solidCircle}
                        style={{ verticalAlign: "middle" }}
                      />
                      &nbsp;
                      <Text
                        className="annotationtree__pointName"
                        style={{ color: "black", verticalAlign: "middle" }}
                        title={undefined}
                        ref={(el: HTMLElement | null) => {
                          if (el && el.scrollWidth > el.clientWidth) {
                            el.setAttribute("title", pt.name);
                          } else if (el) {
                            el.removeAttribute("title");
                          }
                        }}
                      >
                        {pt.name}
                      </Text>
                      {pt.name.includes("-AI") && (
                        <FontAwesomeIcon
                          icon={faWandMagicSparkles}
                          color="gray"
                          style={{ marginLeft: "8px" }}
                        />
                      )}
                      {!userCanDeleteAnnotations || isFreehand ? (
                        <></>
                      ) : (
                        <Button data-tooltip={true} appearance={Appearance.MINIMAL}>
                          <Icon name="menu" />
                          <TooltipFlyout placement={"left-start"}>
                            <TooltipLink
                              onClick={onTransition(
                                "all " + pt.count.toString() + " class: " + pt.name + " points",
                                deletePoints(pt, hpf, imageId, dispatch)
                              )}
                            >
                              Delete Points
                            </TooltipLink>
                          </TooltipFlyout>
                        </Button>
                      )}
                    </Box>
                    <Box className="hangRight" style={{ textAlign: "right" }}>
                      <Text
                        onClick={(e: MouseEvent) =>
                          e && e.target && copyText((e.target as HTMLSpanElement).innerText)
                        }
                        style={{
                          backgroundColor: "#d0d0d0",
                          padding: "2px 4px",
                          borderRadius: "3px",
                          minWidth: "40pt",
                          textAlign: "center",
                          display: "inline-block"
                        }}
                      >
                        {pt.count}
                      </Text>
                      {!isFreehand && (
                        <Checkbox
                          checked={
                            !(pt.name.includes("-AI")
                              ? aiHpfVisibility
                              : manualHpfVisibility
                            )?.find(
                              x =>
                                (x.hpfClassid === fragment.id && x.hpfId === null) ||
                                (x.hpfId === hpf.id && x.pointClassId === pt.id)
                            ) &&
                            !manualHpfVisibility?.find(
                              x => x.hpfId === hpf.id && x.pointClassId === null
                            ) &&
                            !aiHpfVisibility?.find(
                              x => x.hpfId === hpf.id && x.pointClassId === null
                            )
                          }
                          onChange={() => {
                            dispatch(
                              toggleHpfVisibility({
                                source: pt.name.includes("-AI") ? "VisioPharm" : "CR",
                                fragment: fragment,
                                hpfVisibility: {
                                  hpfClassid: fragment.id || null,
                                  hpfId: hpf.id,
                                  pointClassId: pt.id
                                }
                              })
                            );
                          }}
                        ></Checkbox>
                      )}
                    </Box>
                  </PointTreeRow>
                ))}
              </Box>
            </div>
          ))}
        </Box>
      </div>
    );
  };

  return (
    <>
      {confirmTransitionDialog}
      <AnnoNextHeader>
        <Text style={{ fontWeight: "bold" }}>Annotations</Text>
      </AnnoNextHeader>

      {/* AI Annotations Accept Checkbox */}
      {hasAiAnnotations && (
        <Box>
          <YellowBackgroundDiv>
            <Checkbox
              checked={acceptAiAnnotations}
              onChange={() => imageId && dispatch(toggleAcceptAiAnnotations(imageId))}
              disabled={!canAcceptAiAnnotations}
            ></Checkbox>
            Include AI Annotation Counts in Reports
          </YellowBackgroundDiv>
          <br />
          {appSequenceId && (
            <Text style={{ paddingLeft: "14px", color: "#3D3E40" }}>
              APP Sequence: {appSequenceId}
            </Text>
          )}
        </Box>
      )}

      <TreeBox>
        <>
          {/* Unified Annotations */}
          {unifiedFragments.map((fragment, index) =>
            renderFragment(fragment, index, `${fragment.isAI ? "ai" : "manual"}_branch_`)
          )}

          {/* Celiac Table */}
          {imageWithAnnotations &&
          "resource" in imageWithAnnotations &&
          imageWithAnnotations.resource.study?.indications.includes(Indication.CeliacDisease) &&
          imageWithAnnotations.resource.multilineAnnotationClassesWithCount.length > 0 ? (
            <div key="InfoSidebarCeliacTable">
              <TreeRow key="InfoSidebarCeliacTableTreeRow" className="annotationtree__treeRow">
                <TreeCellPart>
                  <Box
                    className="annotationtree__treeController annotationtree__treeIsOpen"
                    data-frag-name="InfoSidebarCeliacTable"
                    onClick={(e: MouseEvent) =>
                      e &&
                      e.currentTarget &&
                      toggleOpenTree(
                        e.currentTarget as HTMLElement,
                        "branch_InfoSidebarCeliacTable"
                      )
                    }
                  >
                    <Box className="annotationtree__closedTree">
                      <FontAwesomeIcon icon={faCaretRight} />
                    </Box>
                    <Box className="annotationtree__openTree">
                      <FontAwesomeIcon icon={faCaretDown} />
                    </Box>
                  </Box>
                </TreeCellPart>
                <Box className="annotationtree__treeCell">Celiac Line Measurements</Box>
                <Box className="hangRight">
                  <Checkbox
                    checked={celiacTableVisibility}
                    onChange={() => {
                      dispatch(toggleCeliacTableVisibility());
                    }}
                  ></Checkbox>
                </Box>
              </TreeRow>
              <Box id="branch_InfoSidebarCeliacTable" className="annotationtree__treeBranchOpen">
                <InfoSidebarCeliacTable
                  annotationClasses={
                    imageWithAnnotations.resource.multilineAnnotationClassesWithCount
                  }
                  annotations={imageWithAnnotations.resource.annotations.filter(a => !a.isAi)}
                  imageId={imageWithAnnotations.resource.imageAndQuery.image.id}
                />
              </Box>
            </div>
          ) : null}
        </>
      </TreeBox>
    </>
  );
};

export default UnifiedAnnotationTree;
